/* import __COLOCATED_TEMPLATE__ from './card.hbs'; */
import { action, computed } from '@ember/object';
import { attributeBindings } from '@ember-decorators/component';
import { confirmation } from 'client-portal/utils/modals';
import { createElement, error, success, warning } from 'client-portal/utils/banners';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { isImage } from 'client-portal/utils/file';
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import classic from 'ember-classic-decorator';

const REQUEST_TIMEOUT_MESSAGE = 'Request timeout';
const TIMEOUT = 10000;
const TIMEOUT_ANIMATION_INTERVAL = 1000;
const AMOUNT_TO_FILL_EACH_INTERVAL = 1 / (TIMEOUT / TIMEOUT_ANIMATION_INTERVAL);

@classic
@attributeBindings('side:data-test-insurance-card')
export default class SiteDocumentsContactInfoInsuranceSectionCard extends Component {
  @service intl;
  @service media;
  @service store;
  @service currentPractice;

  @service analytics;
  @reads('demographicsForm.connection.insuranceCards') insuranceCards;
  @reads('demographicsForm.connection.insuranceCardFrontId') insuranceCardFrontId;
  @reads('demographicsForm.connection.insuranceCardBackId') insuranceCardBackId;
  @reads('currentPractice.featureInsuranceTextRecognition') featureInsuranceTextRecognition;

  displayLoadingModal = false;
  textExtractionProgress = 0;

  @computed('side', 'intl.locale')
  get translatedSide() {
    const key = `site.documents.contactInfo.insuranceSection.${this.side.toLowerCase()}`;
    return this.intl.t(key);
  }

  @computed('translatedSide', 'intl.locale')
  get cardTitle() {
    const ofCard = this.intl.t('site.documents.contactInfo.insuranceSection.ofInsuranceCard');
    return `${this.translatedSide} ${ofCard}`;
  }

  @computed('model.documentExt')
  get isImage() {
    return isImage(this.model);
  }

  @computed('insuranceCardFrontId', 'insuranceCardBackId', 'side', 'insuranceCards.[]')
  get model() {
    let id = this.demographicsForm.connection.get(this.mappedField);
    return this.insuranceCards.findBy('id', id?.toString());
  }

  @computed('side')
  get mappedField() {
    return this.side === 'Front' ? 'insuranceCardFrontId' : 'insuranceCardBackId';
  }

  @computed('model.id')
  get fileUrl() {
    if (this.model?.id) {
      if (isEmberTesting()) {
        return '/test-fixtures/test-logo.png';
      }

      let adapter = this.store.adapterFor('insurance-card');
      return adapter.urlForFindRecord(this.model.id, 'insurance-card', {});
    }

    return undefined;
  }

  @action
  async remove() {
    await this.model.destroyRecord();
    this.demographicsForm.connection.set(this.mappedField, null);
    this.demographicsForm.set(this.mappedField, null);
  }

  @(task(function* (file) {
    try {
      let card = yield Promise.resolve(
        this.store
          .createRecord('insurance-card', {
            file,
            connection: this.demographicsForm.connection,
          })
          .save()
      );
      this.analytics.track('Insurance Card Uploaded', {
        object: `Insurance Card`,
        action: 'Uploaded',
        source: window.navigator.userAgent,
        'object_id': card.id,
        'card_type': `${this.side} of Card`,
        'client_profile_page': false,
      });
      this.demographicsForm.connection.set(this.mappedField, card.id);
      this.demographicsForm.set(this.mappedField, card.id?.toString());
      if (!this.featureInsuranceTextRecognition) return;

      if (this._extractionFieldsFilled()) {
        let { dismiss } = yield confirmation({
          title: this.intl.t('messages.insuranceCard.confirmUpdateTitle'),
          text: 'Existing info will be deleted and replaced with info from the new images',
          confirmButtonText: 'Update info',
          cancelButtonText: 'Save without updating',
        });

        if (dismiss) return;
      }
      yield this._extractText(card.id);
    } catch (err) {
      let content = createElement('p', { innerText: err.message });
      error({ title: this.intl.t('messages.error.fileNotUploaded'), content });
    }
  }).drop())
  uploadCard;

  _extractionFieldsFilled() {
    return (
      this.demographicsForm.insuranceMemberId ||
      this.demographicsForm.insurancePlanId ||
      this.demographicsForm.insuranceGroupId
    );
  }

  async _extractText(cardId) {
    this.set('displayLoadingModal', true);
    let intervalId = this._startTextExtractionProgressAnimation();
    try {
      let adapter = this.store.adapterFor('insurance-text-extraction');
      await this._requestWithTimeout(adapter.extractInsuranceCardInfoFromDocument(cardId), TIMEOUT);
      this._fillExtractedFields();
    } catch (error) {
      this._handleExtractionError();
    } finally {
      clearInterval(intervalId);
      this.setProperties({
        displayLoadingModal: false,
        textExtractionProgress: 0,
      });
    }
  }

  _startTextExtractionProgressAnimation() {
    return setInterval(() => {
      if (this.textExtractionProgress + AMOUNT_TO_FILL_EACH_INTERVAL < 1) {
        this.set(
          'textExtractionProgress',
          this.textExtractionProgress + AMOUNT_TO_FILL_EACH_INTERVAL
        );
      }
    }, TIMEOUT_ANIMATION_INTERVAL);
  }

  _handleExtractionError() {
    warning({
      title: this.intl.t('messages.error.cannotAutofillInsuranceInfo'),
      content: createElement('p', {
        innerText: `Information couldn't be filled from the insurance card`,
      }),
    });
  }

  _requestWithTimeout(request, timeout) {
    return Promise.race([request, this._promiseTimeout(timeout)]);
  }

  _promiseTimeout(timeout) {
    return new Promise((_, reject) => {
      // eslint-disable-next-line ember-cleanup/no-settimeout
      setTimeout(() => {
        reject(new Error(REQUEST_TIMEOUT_MESSAGE));
      }, timeout);
    });
  }

  _fillExtractedFields() {
    this._fillField('memberId', 'insuranceMemberId');
    this._fillField('planId', 'insurancePlanId');
    this._fillField('groupId', 'insuranceGroupId');
    if (this._extractionFieldsFilled()) {
      success({ title: this.intl.t('messages.success.insuranceInfoAdded') });
    } else {
      warning({
        title: this.intl.t('messages.error.cannotAutofillInsuranceInfo'),
        content: createElement('p', {
          innerText: `Information couldn't be filled from the insurance card`,
        }),
      });
    }
  }

  _fillField(textExtractionField, demographicsFormField) {
    let fieldValue =
      this._insuranceTextExtractions()
        .map(insuranceTextExtraction =>
          this._getFieldValue(insuranceTextExtraction, textExtractionField)
        )
        .find(value => value) ?? null;

    this.demographicsForm.set(demographicsFormField, fieldValue);
  }

  _getFieldValue(insuranceTextExtraction, textExtractionField) {
    if (insuranceTextExtraction?.[`${textExtractionField}ConfidenceScore`] >= 90) {
      return insuranceTextExtraction[textExtractionField];
    }
    return null;
  }

  _insuranceTextExtractions() {
    return [
      this.insuranceCardFrontId &&
        this.store.peekRecord('insurance-card', this.insuranceCardFrontId).insuranceTextExtraction,
      this.insuranceCardBackId &&
        this.store.peekRecord('insurance-card', this.insuranceCardBackId).insuranceTextExtraction,
    ].filter(Boolean);
  }
}
